<template>
  <!-- Loading Overlay for Initial Load -->
  <div v-if="isInitialLoading" class="fixed inset-0 bg-white flex items-center justify-center z-50">
    <div class="text-center">
      <div class="loader-spinner mb-4"></div>
      <p class="text-[#00423D] text-lg font-medium" style="font-family: 'Open Sans'; font-weight: 500">
        Loading your account...
      </p>
    </div>
  </div>

  <div class="min-h-screen bg-white flex flex-col lg:flex-row-reverse" v-else-if="ownerAccount">
    <div class="p-4 flex-1 flex flex-col md:p-8 lg:px-20 lg:py-6 lg:max-h-screen lg:overflow-y-auto">
      <div class="flex justify-between items-center mt-4">
        <div class="p-1 mb-2">
          <img src="@/assets/onboarding-logo.svg" alt="ask-a-fundraiser-logo" class="inline-block w-36 mb-auto md:w-40 lg:w-56" :style="{ maxWidth: '180px' }" />
        </div>
      </div>

      <h1 class="font-minion text-[32px] leading-[38.4px] text-[#00423D] font-normal mt-5">
        Renew Your Subscription
        <span class="block text-[#00423D] text-[28px] leading-[24px] font-bold mt-0.5 lg:mt-2" style="font-family: 'Open Sans'; font-weight: 700">Continue your fundraising journey</span>
      </h1>

      <div class="mt-8 flex-1 flex flex-col">
        <div class="max-w-6xl mx-auto w-full">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div v-for="subscription in subscriptionsMonthly" :key="subscription.id" 
              class="bg-white border-2 border-[#00423D] rounded-xl p-8 flex flex-col h-full hover:shadow-xl transition-all duration-300"
              :class="`${hasSelectedPlan == false ? 'shakeX' : ''}`">
              
              <!-- Header Section -->
              <div class="text-center mb-1 lg:h-[180px] flex flex-col justify-start">
                <h2 class="font-minion text-[#00423D] text-[26px] leading-[32px] font-semibold mb-4">
                  {{ subscription.title }}
                </h2>
                <div v-if="subscription.price" class="mb-6">
                  <p class="font-minion font-bold mt-1 text-[#00423D] text-[28px] leading-[24px] text-center">
                    Price: ${{ typeof subscription.price === 'object' ? subscription.price.monthly : subscription.price }} / Monthly
                    <span v-if="typeof subscription.price === 'object' && subscription.price.yearly" class="block text-[28px] leading-[24px] mt-3">
                      or ${{ subscription.price.yearly }} / Yearly
                    </span>
                  </p>
                </div>
                <div v-else class="mb-6 lg:min-h-[80px] flex items-center justify-center">
                  <p class="font-minion font-bold mt-1 text-[#00423D] text-[28px] leading-[24px] text-center">
                    Price: Contact Us
                  </p>
                </div>
              </div>

              <!-- Button Section with Loading State -->
              <div class="mb-8 lg:min-h-[60px] flex items-center justify-center">
                <label v-if="subscription.price"
                  class="block font-bold text-[#3E02A0] text-[18px] py-3 px-6 border-2 border-[#3E02A0] rounded-lg text-center duration-300 cursor-pointer m-auto hover:bg-[#3E02A0] hover:text-white transition-colors"
                  :style="{ width: 'fit-content', fontFamily: 'Open Sans', fontWeight: '500' }"
                  :class="{
                    'bg-[#3E02A0] text-white': selectedPlan?.name == subscription.name,
                    'text-[#3E02A0]': selectedPlan?.name != subscription.name,
                    'opacity-50 cursor-not-allowed': isProcessingPayment,
                  }"
                  @click="!isProcessingPayment && showPlanSelectionPopup(subscription)"
                  for="plan-basic">
                  <span v-if="isProcessingPayment" class="flex items-center gap-2">
                    <div class="loader-spinner-small"></div>
                    Processing...
                  </span>
                  <span v-else>Renew Plan</span>
                </label>
                <label v-if="!subscription.price"
                  class="block font-bold text-[#00423D] text-[18px] py-3 px-6 border-2 border-[#00423D] rounded-lg text-center duration-300 cursor-pointer hover:bg-[#00423D] hover:text-white transition-colors m-auto"
                  :style="{ width: 'fit-content', fontFamily: 'Open Sans', fontWeight: '500' }"
                  :class="{ 
                    'opacity-50 cursor-not-allowed': isContactingUs,
                    'bg-[#00423D] text-white': isContactingUs 
                  }"
                  @click="handleContactUs"
                  for="plan-basic">
                  {{ isContactingUs ? 'Opening Email...' : 'Contact Us' }}
                </label>
              </div>

              <!-- Features Section -->
              <div class="flex-1">
                <div class="space-y-4">
                  <div v-for="(feature, idx) in subscription.features" :key="subscription.name + '-feature-' + idx">
                    <!-- Heading style for FULL TRAINING PROGRAM -->
                    <div v-if="feature.includes('FULL TRAINING PROGRAM')" 
                         class="text-center mb-4">
                      <h3 class="font-minion text-[#00423D] text-[18px] font-semibold mb-2" style="font-family: 'Open Sans'; font-weight: 600">
                        {{ feature }}
                      </h3>
                    </div>
                    
                    <!-- Heading style for LIGHT STRUCTURE + GROUP COACHING with line break -->
                    <div v-else-if="feature.includes('LIGHT STRUCTURE + GROUP COACHING')" 
                         class="mt-8 pt-6 border-t-2 border-gray-200 text-center mb-4">
                      <h3 class="font-minion text-[#00423D] text-[18px] font-semibold mb-2" style="font-family: 'Open Sans'; font-weight: 600">
                        {{ feature }}
                      </h3>
                    </div>
                    
                    <!-- Heading style for SELF-GUIDED E-TOOL© LEARNING with line break -->
                    <div v-else-if="feature.includes('SELF-GUIDED E-TOOL© LEARNING')" 
                         class="mt-8 pt-6 border-t-2 border-gray-200 text-center mb-4">
                      <h3 class="font-minion text-[#00423D] text-[18px] font-semibold mb-2" style="font-family: 'Open Sans'; font-weight: 600">
                        {{ feature }}
                      </h3>
                    </div>
                    
                    <!-- Description text style -->
                    <div v-else-if="feature.includes('Our flagship program') || feature.includes('This cohort model offers') || feature.includes('A flexible, self-paced offering')" 
                         class="mb-4">
                      <p class="text-[#333333] text-[15px] leading-[22px] text-left" 
                         :style="{ fontFamily: 'Open Sans', fontWeight: '400' }">
                        {{ feature }}
                      </p>
                    </div>
                    
                    <!-- Bullet point style for list items -->
                    <div v-else class="flex items-start">
                      <div class="flex-shrink-0 mr-4 mt-1">
                        <svg class="w-6 h-6" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#00423D"/>
                        </svg>
                      </div>
                      <p class="text-[#333333] text-[15px] leading-[22px] flex-1" 
                         :style="{ fontFamily: 'Open Sans', fontWeight: '400' }">
                        {{ feature }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <transition name="auth-form">
            <p class="text-center mt-6 text-[16px] text-red-500" 
               :style="{ fontFamily: 'Open Sans', fontWeight: '500' }" 
               v-if="hasSelectedPlan == false">
              Please Select a Plan!
            </p>
          </transition>
        </div>
      </div>
    </div>

    <!-- Plan Selection Popup with Loading State -->
    <div v-if="showPopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="font-minion text-[24px] text-[#00423D] font-semibold">Choose Your Renewal Plan</h3>
          <button @click="closePopup" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="border border-[#00423D] rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
               :class="{ 'opacity-50 cursor-not-allowed': isProcessingPayment }"
               @click="!isProcessingPayment && selectPlan('month')">
            <div class="flex justify-between items-center">
              <div>
                <h4 class="font-minion text-[18px] text-[#00423D] font-semibold">Monthly Plan</h4>
                <p class="text-[#666666] text-[14px] mt-1">Billed monthly</p>
              </div>
              <div class="text-right">
                <p class="font-minion text-[24px] text-[#00423D] font-bold">$150</p>
                <p class="text-[#666666] text-[12px]">per month</p>
              </div>
            </div>
          </div>
          
          <div class="border border-[#00423D] rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
               :class="{ 'opacity-50 cursor-not-allowed': isProcessingPayment }"
               @click="!isProcessingPayment && selectPlan('year')">
            <div class="flex justify-between items-center">
              <div>
                <h4 class="font-minion text-[18px] text-[#00423D] font-semibold">Yearly Plan</h4>
                <p class="text-[#666666] text-[14px] mt-1">Billed annually</p>
                <span class="inline-block bg-green-100 text-green-800 text-[10px] px-2 py-1 rounded mt-1">Save $600</span>
              </div>
              <div class="text-right">
                <p class="font-minion text-[24px] text-[#00423D] font-bold">$1,200</p>
                <p class="text-[#666666] text-[12px]">per year</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="mt-6 flex justify-end">
          <button @click="closePopup" 
                  class="text-[#00423D] font-medium px-4 py-2 rounded"
                  :disabled="isProcessingPayment">
            {{ isProcessingPayment ? 'Processing...' : 'Cancel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Payment Processing Overlay -->
    <div v-if="isProcessingPayment" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
        <div class="loader-spinner mb-6"></div>
        <p class="text-[#666666] text-[16px] mt-6" style="font-family: 'Open Sans'; font-weight: 400">
          Please wait while we redirect you to the payment page...
        </p>
      </div>
    </div>

    <!-- Error Modal -->
    <div v-if="showErrorModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
        <h3 class="font-minion text-[24px] text-[#00423D] font-semibold mb-2">Payment Error</h3>
        <p class="text-[#666666] text-[16px]" style="font-family: 'Open Sans'; font-weight: 400">{{ paymentError }}</p>
        <button @click="closeErrorModal" class="text-[#00423D] font-medium px-4 py-2 rounded mt-4">
          OK
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import global from "@/global";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const authStore = useAuthStore();
const ownerAccount = ref(false);
const selectedPlan = ref(null);
const hasSelectedPlan = ref(null);
const selectedTerm = ref(null);
const showPopup = ref(false);
const currentSubscription = ref(null);

// Add these reactive variables
const paymentError = ref(null);
const showErrorModal = ref(false);

// Loading states
const isInitialLoading = ref(true);
const isProcessingPayment = ref(false);
const isContactingUs = ref(false);

const subscriptionsMonthly = [
  {
    id: "price_1RqBbvGMJT6Yj8BopkF4KBus",
    name: "Pro",
    title: "INDIVIDUAL ORGANIZATION E-TOOL SUBSCRIPTION",
    price: {
      monthly: 150.0,
      yearly: 1200.0
    },
    description: "Pro Plan",
    features: [
      "Strategic Plan for Fundraising",
      "Customizable Case for Support",
      "Gift Pyramid",
      "Stewardship Structure",
      "Policies and Procedures Manual",
      "Goals Narrative",
      "Fundraising Budgets & Revenue Projections (Annual & 3-Year)",
      "Collaboration Tools",
    ],
  },
  {
    name: "Premium",
    title: "FUNDER/AGGREGATE SUPPORTED OFFERINGS",
    description: "Premium Plan",
    price: null, // This will show "Contact Us" button
    features: [
      "FULL TRAINING PROGRAM",
      "Our flagship program is a cohort-based model that combines five interactive training sessions (virtual or in person), 1-year access to our E-Tool©, coaching hours, and personalized assessments and feedback. This Program equips nonprofits with the structure, support, and tools needed to build a tailored fundraising strategy. Over six months, organizations will develop:",
      "A 3-year fundraising plan",
      "A donor gift pyramid and stewardship structure", 
      "A written case for support and funder-ready pitch deck",
      "Development policies and procedures",
      "LIGHT STRUCTURE + GROUP COACHING",
      "This cohort model offers a self-paced, yet structured learning experience. Participants are guided through 16 E-Tool Modules at their own pace, while also benefiting from shared onboarding, collective milestones, and group coaching support. The program includes:",
      "A minimum of 10 E-Tool© licenses",
      "Group Introductory session, readiness assessment, and milestone check-ins",
      "Reporting on nonprofit engagement and progress",
      "SELF-GUIDED E-TOOL© LEARNING",
      "A flexible, self-paced offering ideal for individual nonprofits ready to build their fundraising strategy on their own schedule. Organizations subscribe directly and receive immediate, year-long access to the E-Tool©. Funders can underwrite a portion of the cost, offering their nonprofits a reduced rate. This model strikes a balance between autonomy and structure, providing both built-in support and guidance."
    ],
  },
];

function showPlanSelectionPopup(subscription) {
  if (isProcessingPayment.value) return;
  currentSubscription.value = subscription;
  showPopup.value = true;
}

function closePopup() {
  if (isProcessingPayment.value) return;
  showPopup.value = false;
  currentSubscription.value = null;
}

async function selectPlan(term) {
  if (currentSubscription.value && !isProcessingPayment.value) {
    selectedPlan.value = currentSubscription.value;
    selectedTerm.value = term;
    
    // Close popup
    closePopup();
    
    // Process payment immediately
    await processPayment(term);
  }
}

async function processPayment(term) {
  if (selectedPlan.value !== null && term) {
    isProcessingPayment.value = true;
    paymentError.value = null; // Clear previous errors
    
    let data = {
      renewal: {
        plan: selectedPlan.value.name,
        term: term,
      }
    };
    
    try {
      const res = await fetch(`${global.API_URL}/payments/renew`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      
      // Check if response is ok before parsing JSON
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}: ${res.statusText}`);
      }
            
      let response = await res.json();
      
      if (response.data) {
        // Add a small delay to show the processing message
        setTimeout(() => {
          window.location.href = response.data;
        }, 1500);
      } else {
        throw new Error('No payment URL received from server');
      }
    } catch (err) {
      console.error("Payment processing error:", err);
      
      // Handle specific error types
      let userFriendlyMessage = "Payment processing failed. Please try again.";
      
      if (err.message.includes('Network') || err.message.includes('Failed to fetch')) {
        userFriendlyMessage = "Network error. Please check your internet connection and try again.";
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        userFriendlyMessage = "Your session has expired. Please log in again.";
        // Redirect to login
        setTimeout(() => {
          authStore.logout();
          router.push({ name: "login" });
        }, 2000);
      } else if (err.message.includes('422') || err.message.includes('Unprocessable Entity')) {
        userFriendlyMessage = "Invalid plan selection. Please try again.";
      } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
        userFriendlyMessage = "Server error. Please try again later or contact support.";
      } else if (err.message.includes('Stripe')) {
        userFriendlyMessage = "Payment service error. Please try again or contact support.";
      }
      
      paymentError.value = userFriendlyMessage;
      showErrorModal.value = true;
      isProcessingPayment.value = false;
    }
  } else {
    hasSelectedPlan.value = false;
  }
}

// Add function to close error modal
function closeErrorModal() {
  showErrorModal.value = false;
  paymentError.value = null;
}

function handleContactUs() {
  if (isContactingUs.value) return; // Prevent multiple clicks
  
  isContactingUs.value = true;
  
  setTimeout(() => {
    window.open('mailto:info@askafundraiser.com', '_blank');
    isContactingUs.value = false;
  }, 1000);
}

// Check if user is returning from payment (you might need to adjust this based on your payment flow)
onMounted(async () => {
  isInitialLoading.value = true;
  
  try {
    const res = await fetch(`${global.API_URL}/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    let response = await res.json();
    
    // Extract user data from the nested structure
    const data = response.data || response;
    
    // Check if user is owner - only owners can access renewal page
    if (data.role !== "owner") {
      router.push({ name: "settings" });
      return;
    }
    
    // Show renewal page for owner users only
    ownerAccount.value = true;
    isInitialLoading.value = false;
  } catch (err) {
    console.error("Error checking user role:", err);
    // Redirect to settings page on error
    router.push({ name: "settings" });
  } finally {
    isInitialLoading.value = false;
  }
});
</script>

<style scoped>
.auth-form-enter-active,
.auth-form-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.auth-form-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.auth-form-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.shakeX {
  animation: shakeX 0.5s ease-in-out;
}

@keyframes shakeX {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

/* Loading Spinner Styles */
.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00423D;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

.loader-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #00423D;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Disable pointer events when loading */
.opacity-50 {
  pointer-events: none;
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style> 